import { useLangFile } from '../../context/LanguageContext';
import React, { Fragment, memo, useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { Edit } from '@mui/icons-material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { withStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import moment from 'moment-timezone';
import Styles from './CreateUpdateSeason.module.less';
import EditTextDialog from '../Modals/EditTextDialog';
import PropTypes from 'prop-types';
import CreateUpdateSeasonDateInput from './CreateUpdateSeasonDateInput';
import {
  Tooltip,
  FormControlLabel,
  FormLabel,
  FormControl,
  Radio,
  RadioGroup,
} from '@mui/material';
import DialogConfirmArchiveSeason from './DialogConfirmArchiveSeason';
import { formatSeason } from '../../helpers/SeasonUtils';

interface CreateUpdateSeasonProps {
  showDialog: boolean;
  enableArchiveButton?: boolean;
  seasonToEdit?: any;
  onDismiss: () => void;
  onPrimary: (name: string, startDate: moment, endDate: moment, copyFields: boolean) => void;
  onArchive: (seasonId: number) => void;
}

const dialogTitleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: [[theme.spacing(2), theme.spacing(2), 0, theme.spacing(2)]],
  },
  title: {
    marginRight: theme.spacing(2),
  },

  editButton: {
    padding: 0,
    color: theme.palette.grey[1000],
  },
});

const DialogTitle = withStyles(dialogTitleStyles)((props) => {
  const { children, classes, onEdit, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Typography variant="h6" className={classes.title}>
          {children}
        </Typography>
        {onEdit ? (
          <IconButton className={classes.editButton} onClick={onEdit}>
            <Edit />
          </IconButton>
        ) : null}
      </Box>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: [[0, theme.spacing(2)]],
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const parseDateFormat = 'YYYY-MM-DD';
const displayDateFormat = 'MMM DD, YYYY';

const defaultStartDateMonth = '08';
const defaultStartDateDay = '01';

const defaultEndDateMonth = '07';
const defaultEndDateDay = '31';

const CreateUpdateSeason: React.FC<CreateUpdateSeasonProps> = ({
  showDialog,
  enableArchiveButton = true,
  seasonToEdit,
  onDismiss,
  onPrimary,
  onArchive,
}) => {
  const LangFile = useLangFile();

  const [startDate, setStartDate] = useState<moment>(
    moment(`${moment().year()}-${defaultStartDateMonth}-${defaultStartDateDay}`, parseDateFormat)
  );
  const [endDate, setEndDate] = useState<moment>(
    moment(`${moment().year() + 1}-${defaultEndDateMonth}-${defaultEndDateDay}`, parseDateFormat)
  );
  const [name, setName] = useState<string>(
    `${LangFile.SeasonUtils.season} ${moment(startDate, parseDateFormat).year()}/${moment(endDate, parseDateFormat).year()}`
  );
  const [renameSeason, setRenameSeason] = useState(false);
  const [showConfirmArchive, setShowConfirmArchive] = useState(false);
  const [copyFields, setCopyFields] = useState(true);
  const edit = seasonToEdit !== null && seasonToEdit !== undefined;
  const primaryTitle = edit ? LangFile.AppSettings.save : LangFile.NoteDialog.create;

  const onStartDateChange = useCallback((date: moment) => {
    setStartDate(date);
  }, []);

  const onEndDateChange = useCallback((date: moment) => {
    setEndDate(date);
  }, []);

  const onPrimaryClick = useCallback(() => {
    onPrimary(name, startDate, endDate, copyFields);
  }, [onPrimary, name, startDate, endDate, copyFields]);

  const onArchiveConfirmed = useCallback(() => {
    onArchive(seasonToEdit.id);
    setShowConfirmArchive(false);
  }, [onArchive, seasonToEdit]);

  const onEditClick = useCallback(() => {
    setRenameSeason(true);
  }, []);

  const handleUpdateName = useCallback((value: string) => {
    if (value && value.length > 0) {
      setName(value);
    }
  }, []);

  useEffect(() => {
    if (seasonToEdit) {
      const seasonStartDate = moment(seasonToEdit.startDate, parseDateFormat);
      const seasonEndDate = moment(seasonToEdit.endDate, parseDateFormat);

      setStartDate(seasonStartDate);
      setEndDate(seasonEndDate);

      const formattedSeasonName = formatSeason(seasonToEdit, LangFile);
      setName(formattedSeasonName);
    }
  }, [seasonToEdit]);

  return (
    <Fragment>
      <Dialog onClose={onDismiss} open={showDialog}>
        <DialogTitle onEdit={onEditClick}>{name}</DialogTitle>
        <DialogContent>
          <p>{LangFile.CreateUpdateSeason.info1}</p>
          <p>{LangFile.CreateUpdateSeason.info2}</p>
          <ul>
            <li>{LangFile.CreateUpdateSeason.crops}</li>
            <li>{LangFile.CreateUpdateSeason.sowingDates}</li>
            <li>{LangFile.CreateUpdateSeason.prescriptionMaps}</li>
            <li>{LangFile.CreateUpdateSeason.soilSurveys}</li>
          </ul>
          <p>{LangFile.CreateUpdateSeason.datesInfo}</p>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: '18px',
              }}
            />
            <CreateUpdateSeasonDateInput
              title={LangFile.CreateUpdateSeason.start}
              date={startDate}
              maxDate={endDate}
              onChanged={onStartDateChange}
              format={displayDateFormat}
            />
            <Box
              sx={{
                width: '18px',
              }}
            />
            <CreateUpdateSeasonDateInput
              title={LangFile.CreateUpdateSeason.end}
              date={endDate}
              minDate={startDate}
              onChanged={onEndDateChange}
              format={displayDateFormat}
            />
          </Box>

          {/* Insert toggle for copying all fields to new season */}
          {!edit && (
            <FormControl style={{ marginTop: '32px', display: 'flex' }}>
              <FormLabel style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                {LangFile.CreateUpdateSeason.copyFields}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                onChange={(event) => {
                  setCopyFields(event.target.value === 'yes');
                }}
                style={{ justifyContent: 'center', marginTop: '8px' }}
                value={copyFields ? 'yes' : 'no'}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color={'primary'} />}
                  label={LangFile.CreateUpdateSeason.yes}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color={'primary'} />}
                  label={LangFile.CreateUpdateSeason.no}
                />
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions className={Styles.DialogActions}>
          {edit && (
            <Tooltip
              title={
                enableArchiveButton
                  ? LangFile.CreateUpdateSeason.archiveButtonEnabledTooltip
                  : LangFile.CreateUpdateSeason.archiveButtonDisabledTooltip
              }
              placement={'bottom-start'}
            >
              <div>
                <Button
                  color={'secondary'}
                  onClick={setShowConfirmArchive}
                  disabled={!enableArchiveButton}
                >
                  {LangFile.CreateUpdateSeason.archiveSeason}
                </Button>
              </div>
            </Tooltip>
          )}
          {!edit && <Box />}
          <Box
            sx={{
              display: 'flex',
              pt: 2,
            }}
          >
            <Button onClick={onDismiss} color={'inherit'}>
              {LangFile.DateRangeInput.cancel}
            </Button>
            <Box
              sx={{
                width: '8px',
              }}
            />
            <Button onClick={onPrimaryClick} color={'primary'} variant={'contained'}>
              {primaryTitle}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <EditTextDialog
        title={LangFile.WeatherStationMenu.rename}
        open={Boolean(renameSeason)}
        value={name}
        defaultValue={name}
        placeholder={LangFile.SeasonUtils.seasonName}
        onUpdate={(newName) => handleUpdateName(newName)}
        onClose={() => setRenameSeason(false)}
      />

      <DialogConfirmArchiveSeason
        onConfirm={onArchiveConfirmed}
        onClose={setShowConfirmArchive}
        open={Boolean(showConfirmArchive)}
      />
    </Fragment>
  );
};

export default memo(CreateUpdateSeason);
